import * as _ from "lodash";

import { PString } from "../properties/PString";
import { PVector2 } from "../properties/PVector2";
import { PTexture } from "../properties/PTexture";
import { PNumber } from "../properties/PNumber";
import { PThreeConstant } from "../properties/PThreeConstant";
import { PColor } from "../properties/PColor";
import { PBoolean } from "../properties/PBoolean";
import { PAny } from "../properties/PAny";
import { AbstractProperty } from "../properties/AbstractProperty";
import { json } from "../../global";
import { MaterialPreview } from "../../ui/components/MaterialPreview";
import { AbstractMetaJsonNode } from "./AbstractMetaJsonNode";
import { ThreeMixin } from "./ThreeMixin";
import { GenericMetaNodeConstructor } from "./GenericMetaNode";
import * as Commons from "oc-commons/Commons";
import * as Maths from "oc-commons/Maths";
import { PVector2Array } from "../properties/PVector2Array";
import { Material, MathUtils } from "three";
import * as THREE from "three";

const threeMaterialClassToEmptyInstanceMap = Commons.cacheWeak((constructor) => new constructor());

export class MaterialJson extends ThreeMixin<GenericMetaNodeConstructor, Material>(
  AbstractMetaJsonNode,
) {
  _previousTypeConstructor: typeof Material.constructor;
  updateSceneCalled = false;
  id: number;
  _Type: string;
  WrapS: string;
  WrapT: string;
  Rotation: number;
  Repeat: [number, number];
  Center: [number, number];
  Offset: [number, number];
  FlipY: boolean;
  Comment: string;

  // constructor(metaTable, meta) {
  //     super(metaTable, meta);
  //     // this.threeValue = threeValue;
  //     // this.threeValue = threeValue ?? new THREE.MeshBasicMaterial();
  //     // this.fromThree(this.threeValue);
  //     // folgendes, damit die Vergleichswerte vorher/nachher initialisiert sind
  //     // this.changed('json', () => this.toJSON());
  //     // this.changed('Type');
  // }

  get Type() {
    return this._Type;
  }

  set Type(type) {
    if (this._Type == type) {
      return;
    }
    this._Type = type;
    this.createDefaultThreeValue();
  }

  createDefaultThreeValue() {
    this._Type = this._Type ?? "MeshStandardMaterial";
    this.threeValue = new THREE[this._Type]();
  }

  fillWithDefaults() {
    this.fromThree(this.threeValue);
    return this;
  }

  fromJSON(json: json = {}, filter = (p) => p.type) {
    this.migrateJsonFrom133To135(json);
    this.Type = json["Type"];
    super.fromJSON(json);
    return this;
  }

  migrateJsonFrom133To135(json) {
    if (json.sheenColor == null && json.sheenTint != null) {
      json.sheenColor =
        json.sheen < 0.00001 && json.sheenTint == "#000000" ? "#ffffff" : json.sheenTint;
    }
    if (json.attenuationColor == null && json.attenuationTint != null) {
      json.attenuationColor = json.attenuationTint;
    }
    if (json.specularColor == null && json.specular != null) {
      json.specularColor = json.specular;
    }
    if (json.specularColorMap == null && json.specularMap != null) {
      json.specularColorMap = json.specularMap;
    }
  }

  getProperties() {
    if (
      this._properties == null ||
      this._previousTypeConstructor !== this.threeValue?.constructor
    ) {
      this._resetProperties();
      this._previousTypeConstructor = this.threeValue?.constructor;
    }
    return this._properties;
  }

  init() {
    super.init();
    this.toThree(this.threeValue);
    return this;
  }

  toThree(threeValue) {
    super.toThree(threeValue);
    // Workaround: in three r133 erfordert .map = null ein delaytes threeValue.needsUpdate=true:
    _.delay(() => (this.threeValue.needsUpdate = true), 1);
  }

  updateScene() {
    this.updateSceneCalled = true;
    this._dataChanged = {};
    if (!this.changed("json", () => this.toJSON())) {
      return;
    }
    this.toThree(this.threeValue);
    this.replaceAllMaterialsInScene();
  }

  replaceAllMaterialsInScene() {
    if (this.threeValue.name) {
      APP.sceneImporter.replaceMaterial(this.threeValue);
      STAGE.replaceMaterialByNameInScene(this.threeValue);
    }
  }

  createProperties() {
    if (this.threeValue == null) {
      this.createDefaultThreeValue();
    }

    const hasTexture = (p) =>
      p.node.getProperties().some((p) => p.type == "Texture" && p.get() != null);

    const propertiesNotFromThreeValue: AbstractProperty<any, any>[] = [
      new PAny({
        name: "id",
        type: "Id",
        node: this,
        showInEditor: (p) => false,
        isThree: (p) => false,
      }),
      new PString({
        name: "Type",
        selectOptions: Object.keys(THREE).filter(
          (k) =>
            THREE[k].prototype instanceof Material &&
            !/Sprite|Shader|Points|Depth|Distance|Normal/.test(k),
        ),
        default: "MeshPhysicalMaterial",
        node: this,
        isThree: (p) => false,
        validate: (p) =>
          /Phong|Lambert/.test(p.get())
            ? {
                valid: true,
                className: "warning",
                message:
                  "dieser Type ist veraltet und führt zu Darstellungsfehlern. Bitte verwenden sie statt dessen MeshStandardMaterial oder MeshPhysicalMaterial",
              }
            : undefined,
      }),
      new PString({
        name: "Comment",
        comment: "Freier Kommentar",
        defaultInJson: false,
        node: this,
        isThree: (p) => false,
      }),
      new PVector2({
        name: "Repeat",
        default: [1, 1],
        defaultInJson: false,
        comment:
          "How many times the texture is repeated across the surface, in each direction U and V. If repeat is set greater than 1 in either direction, the corresponding Wrap parameter should also be set to THREE.RepeatWrapping or THREE.MirroredRepeatWrapping to achieve the desired tiling effect. Setting different repeat values for textures is restricted in the same way like .offset.",
        node: this,
        showInEditor: hasTexture,
        isThree: (p) => false,
      }),
      new PString({
        name: "WrapS",
        selectOptions: ["ClampToEdgeWrapping", "RepeatWrapping", "MirroredRepeatWrapping"],
        default: "RepeatWrapping",
        defaultInJson: false,
        comment:
          "This defines how the texture is wrapped horizontally and corresponds to U in UV mapping.\n" +
          "The default is THREE.ClampToEdgeWrapping, where the edge is clamped to the outer edge texels. The other two choices are THREE.RepeatWrapping and THREE.MirroredRepeatWrapping. See the texture constants page for details.",
        node: this,
        showInEditor: hasTexture,
        isThree: (p) => false,
      }),
      new PString({
        name: "WrapT",
        selectOptions: ["ClampToEdgeWrapping", "RepeatWrapping", "MirroredRepeatWrapping"],
        default: "RepeatWrapping",
        defaultInJson: false,
        comment:
          "This defines how the texture is wrapped vertically and corresponds to V in UV mapping.\n" +
          "The same choices are available as for .wrapS : number.\n" +
          "NOTE: tiling of images in textures only functions if image dimensions are powers of two (2, 4, 8, 16, 32, 64, 128, 256, 512, 1024, 2048, ...) in terms of pixels. Individual dimensions need not be equal, but each must be a power of two. This is a limitation of WebGL, not three.js.",
        node: this,
        showInEditor: hasTexture,
        isThree: (p) => false,
      }),
      new PNumber({
        name: "Rotation",
        default: 0,
        defaultInJson: false,
        min: -2 * Math.PI,
        max: 2 * Math.PI,
        inputScale: 180 / Math.PI,
        comment:
          "How much the texture is rotated around the center point, in degree. Positive values are counter-clockwise. Default is 0.",
        node: this,
        showInEditor: hasTexture,
        isThree: (p) => false,
      }),
      new PVector2({
        name: "Center",
        default: [0, 0],
        defaultInJson: false,
        comment:
          "The point around which rotation occurs. A value of (0.5, 0.5) corresponds to the center of the texture. Default is (0, 0), the lower left.",
        node: this,
        showInEditor: hasTexture,
        isThree: (p) => false,
      }),
      new PVector2({
        name: "Offset",
        default: [0, 0],
        defaultInJson: false,
        comment:
          "How much a single repetition of the texture is offset from the beginning, in each direction U and V. Typical range is 0.0 to 1.0.",
        node: this,
        showInEditor: hasTexture,
        isThree: (p) => false,
      }),
      new PBoolean({
        name: "FlipY",
        default: true,
        comment:
          "If set to true, the texture is flipped along the vertical axis when uploaded to the GPU. Default is true. For GLTF imports, it must be false.",
        node: this,
        showInEditor: hasTexture,
        isThree: (p) => false,
      }),
    ];

    // genericProps: per Reflection aus dem ThreeValue ermittelte Properties

    const propertyGruppierungMap = new Map();
    MaterialJson.propertyGruppierung.forEach((p) =>
      propertyGruppierungMap.set(p.nameThree ?? p.name, p),
    );
    const genericProps = this.getGenericPropertiesFromThreejs(this.threeValue, (key, value) => {
      const ignoredKey =
        /^(precision|shadowSide|blending|vertexColors|format|blend.*|depthFunc|stencil.*|clip.*|colorWrite|polygon.*|dithering|alphaToCoverage|premultipliedAlpha|version|combine)$/g.test(
          key,
        );
      if (ignoredKey) {
        return new PAny({ name: key, node: this, default: this.threeValue[key] });
      }
      if (propertyGruppierungMap.has(key)) {
        // @ts-ignore
        const { propertyClass, ...propertyGruppierung } = {
          ...propertyGruppierungMap.get(key),
          node: this,
        };
        if (propertyClass == null) {
          return null;
        }
        delete propertyGruppierung.propertyClass;
        const instance = new propertyClass(propertyGruppierung);
        return instance;
      }
      if (key.endsWith("Map")) {
        return new PTexture({
          name: key,
          contentPath: "MaterialJson",
          node: this,
        });
      }
      return undefined;
    });

    // genericProps: default Werte aus Three Values ermitteln (die wurden z.B. bei R133->135 geändert bei einer Farbe)
    const emptyInstance = threeMaterialClassToEmptyInstanceMap(this.threeValue.constructor);
    genericProps.forEach((p) => {
      p.defaultInJson = false;
      p.default = p.threeToInternalFormat(emptyInstance[p.getNameThree()]);
    });

    // Zusammenführen
    const properties = [].concat(propertiesNotFromThreeValue).concat(genericProps);
    return this.orderAndGroup(MaterialJson.propertyGruppierung, properties);
  }

  getTableName() {
    return "MaterialJson";
  }

  /**
   * Dient der Strukturierung und Ergänzung vorhandener Properties, welche mittels Reflektion aus threeValue ermittelt werden
   */
  static propertyGruppierung = [
    { group: "Preview", open: true },
    {
      render: MaterialPreview,
      props: (node, setSelected) => ({ node, setSelected }),
    },
    { group: "Hauptparameter", open: true },
    { name: "id" },
    { name: "Type" },
    { name: "name", propertyClass: PString, comment: "name of the material" },
    {
      name: "color",
      propertyClass: PColor,
      comment: "Color of the material, by default set to white (0xffffff).",
    },
    { name: "map", label: "colorMap", propertyClass: PTexture, contentPath: "MaterialJson" },
    { name: "Comment" },

    { group: "Oberfläche" },
    {
      name: "roughness",
      propertyClass: PNumber,
      min: 0,
      max: 1,
      comment:
        "How rough the material appears. 0.0 means a smooth mirror reflection, 1.0 means fully diffuse. Default is 1.0. If roughnessMap is also provided, both values are multiplied.",
    },
    {
      name: "roughnessMap",
      propertyClass: PTexture,
      contentPath: "MaterialJson",
      comment: "The green channel of this texture is used to alter the roughness of the material.",
    },
    { separator: true },
    {
      name: "metalness",
      propertyClass: PNumber,
      min: 0,
      max: 1,
      comment:
        "How much the material is like a metal. Non-metallic materials such as wood or stone use 0.0, metallic use 1.0, with nothing (usually) in between. Default is 0.0. A value between 0.0 and 1.0 could be used for a rusty metal look. If metalnessMap is also provided, both values are multiplied.",
    },
    {
      name: "metalnessMap",
      propertyClass: PTexture,
      contentPath: "MaterialJson",
      comment: "The blue channel of this texture is used to alter the metalness of the material.",
    },

    { group: "Relief" },
    {
      name: "bumpMap",
      propertyClass: PTexture,
      contentPath: "MaterialJson",
      comment:
        "The texture to create a bump map. The black and white values map to the perceived depth in relation to the lights. Bump doesn't actually affect the geometry of the object, only the lighting. If a normal map is defined this will be ignored.",
    },
    {
      name: "bumpScale",
      propertyClass: PNumber,
      min: 0,
      max: 1,
      comment: "How much the bump map affects the material. Typical ranges are 0-1. Default is 1.",
      showInEditor: (p) => p.node["bumpMap"] != null,
    },
    { separator: true },
    {
      name: "normalMap",
      propertyClass: PTexture,
      contentPath: "MaterialJson",
      comment:
        "The texture to create a normal map. The RGB values affect the surface normal for each pixel fragment and change the way the color is lit. Normal maps do not change the actual shape of the surface, only the lighting. In case the material has a normal map authored using the left handed convention, the y component of normalScale should be negated to compensate for the different handedness.",
    },
    {
      name: "normalMapType",
      propertyClass: PThreeConstant,
      selectOptions: ["TangentSpaceNormalMap", "ObjectSpaceNormalMap"],
      default: "TangentSpaceNormalMap",
      comment: "should be TangentSpaceNormalMap",
      showInEditor: (p) => p.node["normalMap"] != null,
    },
    {
      name: "normalScale",
      propertyClass: PVector2,
      comment:
        "How much the normal map affects the material. Typical ranges are 0-1. Default is a Vector2 set to (1,1).",
      showInEditor: (p) => p.node["normalMap"] != null,
    },
    { separator: true },
    {
      name: "displacementMap",
      propertyClass: PTexture,
      contentPath: "MaterialJson",
      comment:
        "The displacement map affects the position of the mesh's vertices. Unlike other maps which only affect the light and shade of the material the displaced vertices can cast shadows, block other objects, and otherwise act as real geometry. The displacement texture is an image where the value of each pixel (white being the highest) is mapped against, and repositions, the vertices of the mesh.",
    },
    {
      name: "displacementScale",
      propertyClass: PNumber,
      comment:
        "How much the displacement map affects the mesh (where black is no displacement, and white is maximum displacement). Without a displacement map set, this value is not applied. Default is 1.",
      showInEditor: (p) => p.node["displacementMap"] != null,
    },
    {
      name: "displacementBias",
      propertyClass: PNumber,
      comment:
        "The offset of the displacement map's values on the mesh's vertices. Without a displacement map set, this value is not applied. Default is 0.",
      showInEditor: (p) => p.node["displacementMap"] != null,
    },

    { group: "Glas" },
    {
      name: "transmission",
      propertyClass: PNumber,
      min: 0,
      max: 1,
      comment:
        "Degree of transmission (or optical transparency), from 0.0 to 1.0. Default is 0.0.\n" +
        "If the roughness is close to 1, the material wouldn't appear transparent.\n" +
        "Thin, transparent or semitransparent, plastic or glass materials remain largely reflective even if they are fully transmissive. The transmission property can be used to model these materials.\n" +
        "When transmission is non-zero, opacity ('alpha') should be set to 1, and 'transparent' should be set to false",
    },
    {
      name: "transmissionMap",
      propertyClass: PTexture,
      contentPath: "MaterialJson",
      comment:
        "The red channel of this texture is multiplied against .transmission, for per-pixel control over optical transparency. Default is null.",
    },
    { separator: true },
    {
      name: "Reflectivity",
      nameThree: "ior",
      propertyClass: PNumber,
      min: 0,
      max: 1,
      default: 0.5,
      comment:
        "Degree of reflectivity, from 0.0 to 1.0. Default is 0.5, which corresponds to an index-of-refraction of 1.5. This models the reflectivity of non-metallic materials. It has no effect when metalness is 1.0",
      toThree<ThreeInstanceType>(instance: Material) {
        const reflectivity = (this as any).get();
        instance["ior"] = Maths.round((1 + 0.4 * reflectivity) / (1 - 0.4 * reflectivity), 4);
      },
      fromThree<ThreeInstanceType>(instance: Material) {
        const ior = instance["ior"];
        (this as any).set(MathUtils.clamp((2.5 * (ior - 1)) / (ior + 1), 0, 1));
      },
    },
    { separator: true },
    { name: "thickness", propertyClass: PNumber, min: 0, max: 10, comment: "" },
    { name: "thicknessMap", propertyClass: PTexture, contentPath: "MaterialJson", comment: "" },

    { group: "Klarlackbeschichtung" },
    {
      name: "clearcoat",
      propertyClass: PNumber,
      min: 0,
      max: 1,
      comment:
        "Represents the intensity of the clear coat layer, from 0.0 to 1.0. Use clear coat related properties to enable multilayer materials that have a thin translucent layer over the base layer. Default is 0.0.",
    },
    {
      name: "clearcoatMap",
      propertyClass: PTexture,
      contentPath: "MaterialJson",
      comment:
        "The red channel of this texture is multiplied against .clearcoat, for per-pixel control over a coating's intensity. Default is null.",
    },
    { separator: true },
    {
      name: "clearcoatNormalMap",
      propertyClass: PTexture,
      contentPath: "MaterialJson",
      comment:
        "Can be used to enable independent normals for the clear coat layer. Default is null.",
    },
    {
      name: "clearcoatNormalScale",
      propertyClass: PVector2,
      comment:
        "How much .clearcoatNormalMap affects the clear coat layer, from (0,0) to (1,1). Default is (1,1).",
      showInEditor: (p) => p.node["clearcoatNormalMap"] != null,
    },
    { separator: true },
    {
      name: "clearcoatRoughness",
      propertyClass: PNumber,
      min: 0,
      max: 1,
      comment: "Roughness of the clear coat layer, from 0.0 to 1.0. Default is 0.0.",
    },
    {
      name: "clearcoatRoughnessMap",
      propertyClass: PTexture,
      contentPath: "MaterialJson",
      comment:
        "The green channel of this texture is multiplied against .clearcoatRoughness, for per-pixel control over a coating's roughness. Default is null.",
    },

    { group: "Stoffglanz / Satin" },
    {
      name: "sheen",
      propertyClass: PNumber,
      min: 0,
      max: 1,
      comment: "The intensity of the sheen layer, from 0.0 to 1.0. Default is 0.0.",
    },
    {
      name: "sheenColor",
      propertyClass: PColor,
      comment: "The sheen tint",
      showInEditor: (p) => p.node["sheen"] > 0.00001,
    },
    {
      name: "sheenColorMap",
      propertyClass: PTexture,
      contentPath: "MaterialJson",
      comment:
        "The RGB channels of this texture are multiplied against .sheenColor, for per-pixel control over sheen tint. Default is null.",
      showInEditor: (p) => p.node["sheen"] > 0.00001,
    },
    {
      name: "sheenRoughness",
      propertyClass: PNumber,
      min: 0,
      max: 1,
      comment: "Roughness of the sheen layer, from 0.0 to 1.0. Default is 1.0.",
      showInEditor: (p) => p.node["sheen"] > 0.00001,
    },
    {
      name: "sheenRoughnessMap",
      propertyClass: PTexture,
      contentPath: "MaterialJson",
      comment:
        "The alpha channel of this texture is multiplied against .sheenRoughness, for per-pixel control over sheen roughness. Default is null.",
      showInEditor: (p) => p.node["sheen"] > 0.00001,
    },
    { group: "Selbstleuchtend" },
    {
      name: "emissive",
      propertyClass: PColor,
      comment:
        "Emissive (light) color of the material, essentially a solid color unaffected by other lighting. Default is black.",
    },
    {
      name: "emissiveIntensity",
      propertyClass: PNumber,
      min: 0,
      max: 10,
      comment: "Intensity of the emissive light. Modulates the emissive color. Default is 1.",
    },
    {
      name: "emissiveMap",
      propertyClass: PTexture,
      contentPath: "MaterialJson",
      comment:
        "Set emisssive (glow) map. Default is null. The emissive map color is modulated by the emissive color and the emissive intensity. If you have an emissive map, be sure to set the emissive color to something other than black.",
    },

    { group: "Iridescence" },
    {
      name: "iridescence",
      propertyClass: PNumber,
      comment: "undocumented (r148)",
    },
    {
      name: "iridescenceIOR",
      propertyClass: PNumber,
      comment: "undocumented (r148)",
    },
    {
      name: "iridescenceThicknessRange",
      propertyClass: PVector2Array,
      comment: "undocumented (r148)",
    },

    { group: "Texturparameter" },
    { name: "Repeat" },
    { name: "WrapS" },
    { name: "WrapT" },
    { name: "Center" },
    { name: "Offset" },
    { name: "Rotation" },
    { name: "FlipY" },

    { group: "Lightmaps & Baking" },
    {
      name: "aoMap",
      propertyClass: PTexture,
      contentPath: "MaterialJson",
      comment:
        "The red channel of this texture is used as the ambient occlusion map. Default is null. The aoMap requires a second set of UVs.",
    },
    {
      name: "aoMapIntensity",
      propertyClass: PNumber,
      min: 0,
      max: 1,
      comment:
        "Intensity of the ambient occlusion effect. Default is 1. Zero is no occlusion effect.",
      showInEditor: (p) => p.node["aoMap"] != null,
    },
    { separator: true },
    {
      name: "lightMap",
      propertyClass: PTexture,
      contentPath: "MaterialJson",
      comment: "The light map. Default is null. The lightMap requires a second set of UVs.",
    },
    {
      name: "lightMapIntensity",
      propertyClass: PNumber,
      min: 0,
      max: 1,
      comment: "Intensity of the baked light. Default is 1.",
      showInEditor: (p) => p.node["lightMap"] != null,
    },

    { group: "Lichtabfall" },
    {
      name: "attenuationDistance",
      propertyClass: PNumber,
      min: 0,
      max: 1,
      comment: "experimental feature",
    },
    { name: "attenuationColor", propertyClass: PColor, comment: "experimental feature" },

    { group: "Wirkung der Umgebung (EnvMap)" },
    {
      name: "envMap",
      propertyClass: PTexture,
      contentPath: "MaterialJson",
      comment:
        "The environment map. To ensure a physically correct rendering, you should only add environment maps which were preprocessed by PMREMGenerator. Default is null.",
    },
    {
      name: "envMapIntensity",
      propertyClass: PNumber,
      min: 0,
      max: 1,
      comment: "Scales the effect of the environment map by multiplying its color.",
    },

    { group: "Glanzpunkt (Specular)" },
    {
      name: "specularColor",
      propertyClass: PColor,
      comment:
        "Specular color of the material. Default is a Color set to 0x111111 (very dark grey).\n" +
        "This defines how shiny the material is and the color of its shine.",
    },
    {
      name: "specularColorMap",
      propertyClass: PTexture,
      contentPath: "MaterialJson",
      comment:
        "The specular map value affects both how much the specular surface highlight contributes and how much of the environment map affects the surface. Default is null.",
    },
    { separator: true },
    {
      name: "specularIntensity",
      propertyClass: PNumber,
      min: 0,
      max: 1,
      comment: "",
    },
    {
      name: "specularIntensityMap",
      propertyClass: PTexture,
      contentPath: "MaterialJson",
      comment: "",
    },
    { separator: true },
    { name: "specularTint", propertyClass: PColor, comment: "" },
    { name: "specularTintMap", propertyClass: PTexture, contentPath: "MaterialJson", comment: "" },
    {
      name: "shininess",
      propertyClass: PNumber,
      min: 0,
      max: 1000,
      comment:
        "How shiny the .specular highlight is; a higher value gives a sharper highlight. Default is 30.",
    },

    { group: "Line" },
    {
      name: "linewidth",
      propertyClass: PNumber,
      min: 0,
      max: 10,
      comment:
        "Controls line thickness. Default is 1. Due to limitations of the OpenGL Core Profile with the WebGL renderer on most platforms linewidth will always be 1 regardless of the set value.",
    },
    {
      name: "linecap",
      propertyClass: PString,
      selectOptions: ["butt", "round", "square"],
      comment:
        "Define appearance of line ends. Possible values are 'butt', 'round' and 'square'. Default is 'round'.\n" +
        "This corresponds to the 2D Canvas lineCap property and it is ignored by the WebGL renderer.",
    },
    {
      name: "linejoin",
      propertyClass: PString,
      selectOptions: ["round", "bevel", "miter"],
      comment:
        "Define appearance of line joints. Possible values are 'round', 'bevel' and 'miter'. Default is 'round'.\n" +
        "This corresponds to the 2D Canvas lineJoin property and it is ignored by the WebGL renderer.",
    },
    {
      name: "dashSize",
      propertyClass: PNumber,
      min: 0,
      max: 100,
      comment: "The size of the dash. This is both the gap with the stroke. Default is 3.",
    },
    {
      name: "scale",
      propertyClass: PNumber,
      min: 0,
      max: 100,
      comment: "The scale of the dashed part of a line. Default is 1.",
    },
    {
      name: "gapSize",
      propertyClass: PNumber,
      min: 0,
      max: 100,
      comment: "The size of the gap. Default is 1.",
    },

    { group: "Sichtbarkeit" },
    {
      name: "visible",
      propertyClass: PBoolean,
      comment: "Defines whether this material is visible. Default is true.",
    },
    {
      name: "side",
      propertyClass: PThreeConstant,
      selectOptions: ["FrontSide", "BackSide", "DoubleSide"],
      comment:
        "Defines which side of faces will be rendered - front, back or both. Default is THREE.FrontSide. Other options are THREE.BackSide and THREE.DoubleSide.",
    },
    { separator: true },
    {
      name: "transparent",
      propertyClass: PBoolean,
      comment:
        "Defines whether this material is transparent. This has an effect on rendering as transparent objects need special treatment and are rendered after non-transparent objects.\n" +
        "When set to true, the extent to which the material is transparent is controlled by setting its opacity property.\n" +
        "Default is false.",
    },
    {
      name: "opacity",
      propertyClass: PNumber,
      label: "alpha",
      min: 0,
      max: 1,
      comment:
        "Float in the range of 0.0 - 1.0 indicating how transparent the material is. A value of 0.0 indicates fully transparent, 1.0 is fully opaque.\n" +
        "If the material's transparent property is not set to true, the material will remain fully opaque and this value will only affect its color.\n" +
        "Default is 1.0.",
      showInEditor: (p) => p.node["transparent"],
    },
    {
      name: "alphaMap",
      propertyClass: PTexture,
      contentPath: "MaterialJson",
      comment:
        "The alpha map is a grayscale texture that controls the opacity across the surface (black: fully transparent; white: fully opaque). Default is null.\n" +
        "Only the color of the texture is used, ignoring the alpha channel if one exists. For RGB and RGBA textures, the WebGL renderer will use the green channel when sampling this texture due to the extra bit of precision provided for green in DXT-compressed and uncompressed RGB 565 formats. Luminance-only and luminance/alpha textures will also still work as expected.",
      showInEditor: (p) => p.node["transparent"],
    },
    {
      name: "alphaTest",
      propertyClass: PNumber,
      min: 0,
      max: 1,
      comment:
        "Sets the alpha value to be used when running an alpha test. The material will not be rendered if the opacity is lower than this value. Default is 0.",
      showInEditor: (p) => p.node["transparent"],
    },
    { separator: true },
    {
      name: "depthTest",
      propertyClass: PBoolean,
      comment:
        "Whether to have depth test enabled when rendering this material. Default is true.",
    },
    {
      name: "depthWrite",
      propertyClass: PBoolean,
      comment:
        "Whether rendering this material has any effect on the depth buffer. Default is true. When drawing 2D overlays it can be useful to disable the depth writing in order to layer several things together without creating z-index artifacts.",
    },
    {
      name: "wireframe",
      propertyClass: PBoolean,
      comment:
        "Render geometry as wireframe. Default is false.",
    },
    {
      name: "wireframeLinewidth",
      propertyClass: PNumber,
      min: 0,
      comment:
        "Controls wireframe thickness. Default is 1. Due to limitations of the OpenGL Core Profile on most platforms, linewidth will always be 1 regardless of the set value.",
      showInEditor: (p) => p.node["wireframe"],
    },
    {
      name: "wireframeLinecap",
      propertyClass: PString,
      selectOptions: ["butt", "round", "square"],
      comment:
        "Define appearance of line ends. Possible values are 'butt', 'round' and 'square'. Default is 'round'.",
      showInEditor: (p) => p.node["wireframe"],
    },
    {
      name: "wireframeLinejoin",
      propertyClass: PString,
      selectOptions: ["round", "bevel", "miter"],
      comment:
        "Define appearance of line joints. Possible values are 'round', 'bevel' and 'miter'. Default is 'round'.",
      showInEditor: (p) => p.node["wireframe"],
    },
    { separator: true },
    {
      name: "fog",
      propertyClass: PBoolean,
      comment: "Whether the material is affected by fog. Default is true.",
    },
    {
      name: "flatShading",
      propertyClass: PBoolean,
      comment: "Define whether the material is rendered with flat shading. Default is false.",
    },
    {
      name: "toneMapped",
      propertyClass: PBoolean,
      comment:
        "Defines whether this material is tone mapped according to the renderer's toneMapping setting. Default is true.",
    },

    { group: "Sonstiges" },
    {
      name: "matcap",
      propertyClass: PTexture,
      contentPath: "MaterialJson",
      comment: "The matcap map. Default is null.",
    },
    {
      name: "farDistance",
      propertyClass: PNumber,
      min: 0,
      comment: "The far value of the point light's internal shadow camera.",
    },
    {
      name: "gradientMap",
      propertyClass: PTexture,
      contentPath: "MaterialJson",
      comment:
        "Gradient map for toon shading. It's required to set Texture.minFilter and Texture.magFilter to THREE.NearestFilter when using this type of texture. Default is null.",
    },
  ];
}
